import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  ShieldCheck,
  LogOut,
  Loader2,
  Inbox,
  RefreshCw,
  Mail,
  Phone,
  Download,
} from "lucide-react";
import { useAdminAuth } from "./AuthContext";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-black/5 bg-white p-5">
    <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661]">{label}</p>
    <p className="font-serif text-3xl text-[#2D332F] mt-2">{value}</p>
  </div>
);

const Tabs = ({ tab, setTab }) => {
  const items = [
    { k: "appointments", label: "Appointment requests", icon: Calendar },
    { k: "consents", label: "Session consent log", icon: ShieldCheck },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const Icon = it.icon;
        const active = tab === it.k;
        return (
          <button
            key={it.k}
            onClick={() => setTab(it.k)}
            data-testid={`admin-tab-${it.k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm tracking-wide transition ${
              active
                ? "bg-[#2D332F] text-[#FDFCF8]"
                : "border border-black/10 text-[#2D332F] hover:bg-black/5"
            }`}
          >
            <Icon size={14} strokeWidth={1.5} />
            {it.label}
          </button>
        );
      })}
    </div>
  );
};

const AppointmentsTable = ({ rows }) => {
  if (!rows.length) {
    return (
      <div
        className="rounded-2xl border border-dashed border-black/15 p-10 text-center text-[#5C6661]"
        data-testid="admin-appointments-empty"
      >
        <Inbox className="mx-auto mb-3" size={28} strokeWidth={1.4} />
        No appointment requests yet.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="admin-appointments-list">
      {rows.map((r) => (
        <article
          key={r.id}
          data-testid={`admin-appt-${r.id}`}
          className="rounded-2xl border border-black/5 bg-white p-6 hover:shadow-md transition"
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <p className="font-serif text-xl text-[#2D332F]">{r.name}</p>
              <p className="text-xs text-[#5C6661] mt-0.5">{fmt(r.created_at)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs px-2.5 py-1 rounded-full bg-[#F3F1E7] text-[#2D332F]">
                {r.region}
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-[#739072]/15 text-[#5C765B]">
                {r.concern}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
            <a
              href={`mailto:${r.email}`}
              className="inline-flex items-center gap-2 text-[#2D332F] hover:text-[#739072]"
            >
              <Mail size={14} strokeWidth={1.5} /> {r.email}
            </a>
            {r.phone ? (
              <a
                href={`tel:${r.phone}`}
                className="inline-flex items-center gap-2 text-[#2D332F] hover:text-[#739072]"
              >
                <Phone size={14} strokeWidth={1.5} /> {r.phone}
              </a>
            ) : null}
          </div>
          {r.preferred_date ? (
            <p className="text-sm text-[#5C6661] mb-2">
              <span className="text-[#2D332F]/70 mr-1">Preferred:</span>
              {r.preferred_date}
            </p>
          ) : null}
          <p className="text-[#2D332F]/85 leading-relaxed border-t border-black/5 pt-3 mt-3">
            {r.message}
          </p>
          <div className="mt-3 text-xs text-[#5C6661] flex flex-wrap gap-3">
            <span>
              ✓ Privacy consent: {r.consent_privacy ? "yes" : "no"}
            </span>
            <span>
              ✓ Telehealth consent: {r.consent_telehealth ? "yes" : "no"}
            </span>
          </div>
        </article>
      ))}
    </div>
  );
};

const ConsentsTable = ({ rows }) => {
  if (!rows.length) {
    return (
      <div
        className="rounded-2xl border border-dashed border-black/15 p-10 text-center text-[#5C6661]"
        data-testid="admin-consents-empty"
      >
        <ShieldCheck className="mx-auto mb-3" size={28} strokeWidth={1.4} />
        No session consents recorded yet.
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl border border-black/5 bg-white overflow-hidden"
      data-testid="admin-consents-list"
    >
      <table className="w-full text-sm">
        <thead className="bg-[#F3F1E7] text-[#5C6661] text-xs uppercase tracking-[0.18em]">
          <tr>
            <th className="text-left p-4">When</th>
            <th className="text-left p-4">Initials</th>
            <th className="text-left p-4">Meeting ref.</th>
            <th className="text-left p-4">Consents</th>
            <th className="text-left p-4">IP (hash)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`admin-consent-${r.id}`}
              className="border-t border-black/5"
            >
              <td className="p-4 text-[#2D332F] whitespace-nowrap">
                {fmt(r.created_at)}
              </td>
              <td className="p-4 font-serif text-[#2D332F]">
                {r.patient_initials}
              </td>
              <td className="p-4 text-[#2D332F]/85 break-all max-w-[18rem]">
                {r.meeting_ref}
              </td>
              <td className="p-4 text-xs">
                <div className="flex flex-col gap-1">
                  <span>HIPAA: {r.consent_hipaa ? "✓" : "✗"}</span>
                  <span>GDPR: {r.consent_gdpr ? "✓" : "✗"}</span>
                  <span>
                    Recording: {r.consent_recording_disclosure ? "✓" : "✗"}
                  </span>
                </div>
              </td>
              <td className="p-4 text-xs text-[#5C6661] font-mono">
                {r.ip_hash || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function AdminDashboard() {
  const { user, checking, logout, authedAxios } = useAdminAuth();
  const [tab, setTab] = useState("appointments");
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const api = useMemo(() => authedAxios(), [authedAxios]);

  const downloadCsv = async (kind) => {
    try {
      const r = await api.get(`/admin/${kind}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not export CSV.");
    }
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, a, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/appointments"),
        api.get("/admin/consents"),
      ]);
      setStats(s.data);
      setAppointments(a.data || []);
      setConsents(c.data || []);
    } catch (e) {
      setErr(
        e?.response?.data?.detail || "Failed to load dashboard data."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#5C6661]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2D332F]">
      <header className="border-b border-black/5 bg-[#FDFCF8]/90 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="admin-home-link"
            className="flex items-center"
          >
            <img
              src="/logo.jpg"
              alt="Neurological Communication Partners"
              className="h-12 w-auto rounded-xl ring-1 ring-black/5"
            />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-[#5C6661]">
              {user.email}
            </span>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5 transition"
            >
              <RefreshCw size={14} strokeWidth={1.5} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm hover:bg-[#739072] transition"
            >
              <LogOut size={14} strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-3">
            Welcome back, {user.name?.split(" ")[0] || "Kathleen"}
          </p>
          <h1
            data-testid="admin-dashboard-title"
            className="font-serif text-4xl md:text-5xl font-light tracking-tight"
          >
            Dashboard
          </h1>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat
            label="Appointments (all-time)"
            value={
              loading ? "…" : stats?.appointments_total ?? appointments.length
            }
          />
          <Stat
            label="Last 30 days"
            value={loading ? "…" : stats?.appointments_30d ?? "—"}
          />
          <Stat
            label="Consent records"
            value={loading ? "…" : stats?.consents_total ?? consents.length}
          />
        </div>

        <div className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <Tabs tab={tab} setTab={setTab} />
            <button
              onClick={() => downloadCsv(tab)}
              data-testid={`admin-export-${tab}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#739072] text-[#5C765B] text-sm hover:bg-[#739072] hover:text-white transition"
            >
              <Download size={14} strokeWidth={1.5} />
              Export CSV
            </button>
          </div>
          {err && (
            <p
              role="alert"
              data-testid="admin-error"
              className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3"
            >
              {typeof err === "string" ? err : "Error loading data."}
            </p>
          )}
          {loading ? (
            <div className="rounded-2xl border border-black/5 bg-white p-10 flex items-center justify-center text-[#5C6661]">
              <Loader2 className="animate-spin" />
            </div>
          ) : tab === "appointments" ? (
            <AppointmentsTable rows={appointments} />
          ) : (
            <ConsentsTable rows={consents} />
          )}
        </div>

        <p className="mt-12 text-xs text-[#5C6661]">
          Data shown only to authenticated admins. Consent records form your
          HIPAA / GDPR audit trail — exportable on request.
        </p>
      </main>
    </div>
  );
}
